import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../assets/contexts/ThemeContext';

/**
 * 메가메뉴 드롭다운 컴포넌트
 * 역할: 공통 메가메뉴 드롭다운 렌더링 (모든 1depth 항목의 children을 항상 표시)
 */
const MegaMenu = ({ menuData }) => {
  const navigate = useNavigate();
  const megaMenuRef = useRef(null);
  const themeContext = useTheme();
  const themeConfig = themeContext?.themeConfig || (typeof window !== 'undefined' && window.__themeConfig) || {};
  const megaMenuStyle = themeConfig.megaMenuStyle || 'default';

  // href를 React Router 경로로 변환하는 헬퍼 함수
  const convertToReactRoute = (href) => {
    if (!href || href === '#') return null;

    // 외부 URL인 경우 (http://, https://, //)
    if (/^(https?:)?\/\//.test(href)) {
      return null; // 외부 링크는 그대로 사용
    }

    // React Router 경로로 변환
    return href;
  };

  // 메가메뉴 top 위치 업데이트 함수
  const updateMegaMenuTop = () => {
    if (!megaMenuRef.current) return;

    const headerBottom = document.querySelector('.header-bottom');
    let megaMenuTop = 0;

    if (headerBottom) {
      // header-bottom 내부 nav를 우선 사용
      const mainMenuNav = headerBottom.querySelector('.main-menu > nav') ||
        headerBottom.querySelector('nav');
      if (mainMenuNav) {
        const navRect = mainMenuNav.getBoundingClientRect();
        // nav가 0으로 잡히는 경우 header-bottom으로 fallback
        if (navRect.bottom > 0) {
          megaMenuTop = Math.max(0, navRect.bottom);
        } else {
          const targetRect = headerBottom.getBoundingClientRect();
          megaMenuTop = Math.max(0, targetRect.bottom);
        }
      } else {
        const targetRect = headerBottom.getBoundingClientRect();
        megaMenuTop = Math.max(0, targetRect.bottom);
      }
    } else {
      // header-bottom이 없으면 mainMenuNav 사용 (fallback)
      const mainMenuNav = document.querySelector('.main-menu > nav');
      if (mainMenuNav) {
        const navRect = mainMenuNav.getBoundingClientRect();
        megaMenuTop = Math.max(0, navRect.bottom);
      }
    }

    const newValue = Math.round(megaMenuTop);
    if (newValue > 0) {
      megaMenuRef.current.style.top = `${newValue}px`;
      window.headerHeight = newValue;
    }
  };

  // 메가메뉴 위치 업데이트 (마운트 시 및 header-bottom 변경 시)
  useEffect(() => {
    // 초기 위치 설정을 위한 대기 함수들
    const waitForFrame = () => new Promise(resolve => requestAnimationFrame(resolve));

    const waitForDOMReady = async () => {
      if (document.readyState === 'loading') {
        await new Promise(resolve => {
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', resolve);
          } else {
            resolve();
          }
        });
      }
      await waitForFrame();
    };

    const waitForLoad = async () => {
      if (document.readyState !== 'complete') {
        await new Promise(resolve => {
          window.addEventListener('load', resolve, { once: true });
        });
      }
      await waitForFrame();
      await waitForFrame();
    };

    const initialize = async () => {
      // DOM이 준비될 때까지 대기
      await waitForDOMReady();
      await waitForLoad();

      // header-bottom과 메뉴가 준비될 때까지 대기
      let retries = 0;
      const maxRetries = 20;
      while (retries < maxRetries) {
        const headerBottom = document.querySelector('.header-bottom');
        const mainMenuNav = document.querySelector('.main-menu > nav');

        if (headerBottom && mainMenuNav) {
          const headerHeight = headerBottom.offsetHeight || headerBottom.getBoundingClientRect().height;
          const navHeight = mainMenuNav.offsetHeight || mainMenuNav.getBoundingClientRect().height;

          if (headerHeight > 0 && navHeight > 0) {
            // 위치 설정
            updateMegaMenuTop();
            // 여러 프레임에 걸쳐 재확인
            requestAnimationFrame(() => {
              updateMegaMenuTop();
              requestAnimationFrame(() => {
                updateMegaMenuTop();
              });
            });
            break;
          }
        }

        await waitForFrame();
        retries++;
      }
    };

    initialize();

    // MutationObserver로 hidden 클래스 변경 감지 (메가메뉴 표시/숨김 시 즉시 위치 업데이트)
    let mutationObserver = null;
    if (megaMenuRef.current && window.MutationObserver) {
      mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const target = mutation.target;
            // hidden 클래스가 제거되었을 때 (메가메뉴가 표시될 때)
            if (!target.classList.contains('hidden')) {
              // 즉시 위치 업데이트 (여러 프레임에 걸쳐 안정적으로)
              requestAnimationFrame(() => {
                updateMegaMenuTop();
                requestAnimationFrame(() => {
                  updateMegaMenuTop();
                });
              });
            }
          }
        });
      });

      mutationObserver.observe(megaMenuRef.current, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    // ResizeObserver로 header-bottom 크기 변경 감지
    const headerBottom = document.querySelector('.header-bottom');
    let resizeObserver = null;
    if (headerBottom && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          updateMegaMenuTop();
        });
      });
      resizeObserver.observe(headerBottom);
    }

    // 스크롤 및 리사이즈 이벤트
    let pendingUpdate = false;
    const handleScroll = () => {
      if (!pendingUpdate) {
        pendingUpdate = true;
        requestAnimationFrame(() => {
          updateMegaMenuTop();
          pendingUpdate = false;
        });
      }
    };

    const handleResize = () => {
      requestAnimationFrame(() => {
        updateMegaMenuTop();
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (mutationObserver) {
        mutationObserver.disconnect();
      }
    };
  }, []);

  // menuData가 없으면 빈 div만 렌더링 (useMegaMenu 훅에서 참조할 수 있도록)
  if (!menuData || menuData.length === 0) {
    return (
      <div
        className={`mega-menu-dropdown mega-menu-dropdown--${megaMenuStyle}`}
        ref={megaMenuRef}
      ></div>
    );
  }

  const handleMegaMenuClick = async (e, targetUrl) => {
    // 원본 JavaScript 이벤트 리스너가 실행되지 않도록 먼저 처리
    e.preventDefault();
    e.stopPropagation();
    if (e.nativeEvent) {
      e.nativeEvent.stopImmediatePropagation(); // 다른 리스너도 중지
    }

    const route = convertToReactRoute(targetUrl);

    // 메가메뉴 닫기
    if (megaMenuRef.current) {
      megaMenuRef.current.style.opacity = '';
      megaMenuRef.current.style.visibility = '';
      megaMenuRef.current.style.transform = '';
      megaMenuRef.current.style.pointerEvents = '';
      megaMenuRef.current.classList.add('hidden');
    }

    // React Router로 내부 라우팅
    if (route) {
      await new Promise(resolve => requestAnimationFrame(() => resolve()));
      navigate(route);
    } else if (targetUrl) {
      // 외부 링크인 경우
      await new Promise(resolve => requestAnimationFrame(() => resolve()));
      window.location.href = targetUrl;
    }
  };

  return (
    <div className={`mega-menu-dropdown mega-menu-dropdown--${megaMenuStyle}`} ref={megaMenuRef}>
      <div className="container-fluid px-0">
        <div className="row g-0">
          {menuData.map((item, index) => {
            if (!item.children || item.children.length === 0) return null;

            return (
              <div
                key={index}
                className="mega-menu-section col-xl-3 col-lg-4 col-md-6"
                data-menu-index={index}
              >
                <div className="mega-menu-group">
                  {/* 섹션 헤더 (1depth 항목명) */}
                  <div className="mega-menu-header">
                    <span title={item.label || undefined}>{item.label}</span>
                  </div>
                  {/* 2depth 항목들 - children이 있어도 하위 메뉴 표시 안 함, 링크로만 표시 */}
                  <ul className="mega-menu-items">
                    {item.children.map((child, childIndex) => {
                      const firstChild = child.children && child.children.length > 0
                        ? child.children[0]
                        : null;
                      const firstChildHref = firstChild && firstChild.href && firstChild.href !== '#'
                        ? firstChild.href
                        : null;

                      const childHref = child.href && child.href !== '#' ? child.href : '#';
                      const targetUrl = (!childHref || childHref === '#') && firstChildHref
                        ? firstChildHref
                        : (childHref && childHref !== '#' ? childHref : null);

                      const route = convertToReactRoute(targetUrl);

                      return (
                        <li key={childIndex} className="mega-menu-item">
                          {targetUrl && targetUrl !== '#' ? (
                            route ? (
                              <a
                                href={route}
                                className="mega-menu-link"
                                title={child.label || undefined}
                                data-first-child-href={firstChildHref || undefined}
                                onClick={(e) => {
                                  // React 이벤트를 먼저 처리하고 원본 JavaScript가 실행되지 않도록 함
                                  handleMegaMenuClick(e, targetUrl);
                                  // 캡처 단계에서도 이벤트 전파 중지
                                  if (e.nativeEvent) {
                                    e.nativeEvent.stopImmediatePropagation();
                                  }
                                }}
                                onMouseDown={(e) => {
                                  // mousedown 단계에서도 원본 JavaScript 실행 방지
                                  e.stopPropagation();
                                }}
                              >
                                {child.label}
                              </a>
                            ) : (
                              <a
                                href={targetUrl}
                                className="mega-menu-link"
                                title={child.label || undefined}
                                data-first-child-href={firstChildHref || undefined}
                                onClick={(e) => {
                                  handleMegaMenuClick(e, targetUrl);
                                  if (e.nativeEvent) {
                                    e.nativeEvent.stopImmediatePropagation();
                                  }
                                }}
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                {child.label}
                              </a>
                            )
                          ) : (
                            // eslint-disable-next-line jsx-a11y/anchor-is-valid
                            <a
                              href="#"
                              className="mega-menu-link"
                              title={child.label || undefined}
                              data-first-child-href={firstChildHref || undefined}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              {child.label}
                            </a>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MegaMenu;

